// Small steps the parts inside a document share: whether the body can be
// written, undo boundaries, and putting the caret into a table cell.
import type { Editor } from "@tiptap/core";
import { isReadingMode } from "@trameria/editor";

/**
 * Whether the body is writable, counting the phone's reading state as
 * writable (the body is only paused there: a tap starts writing). Parts that
 * must keep working while reading - toggles, the table's controls - ask this,
 * not `editor.isEditable`.
 */
export function canEdit(editor: Editor): boolean {
  return (
    editor.isEditable || (isReadingMode(editor) && editor.options.editable)
  );
}

/**
 * An undo boundary. A live document uses the Y binding's undo manager, which
 * merges changes made within 500ms into one step; adding or removing a row, a
 * column width or a footnote written back should each undo on their own, so
 * this is called before and after them. The manager is reached through the
 * plugin state (its key is "y-undo"). An editor without the Y binding uses
 * prosemirror-history, where an empty transaction with `closeHistory` makes
 * the same boundary (the meta key is the name prosemirror-history gives its
 * first PluginKey).
 */
export function separateUndo(editor: Editor): void {
  const { state, view } = editor;
  const yUndo = state.plugins.find((p) =>
    String((p as unknown as { key?: string }).key ?? "").startsWith("y-undo"),
  );
  const undoManager = yUndo?.getState(state)?.undoManager as
    | { stopCapturing?: () => void }
    | undefined;
  if (undoManager?.stopCapturing) {
    undoManager.stopCapturing();
    return;
  }
  view.dispatch(state.tr.setMeta("closeHistory$", true));
}

/**
 * Puts the caret into the cell at `row`, `col` of the table at `tablePos`. The
 * table commands (`addRowBefore`, …) act on the cell holding the selection,
 * so every one of them goes through this first.
 */
export function selectCell(
  editor: Editor,
  tablePos: number,
  row: number,
  col: number,
): boolean {
  const table = editor.state.doc.nodeAt(tablePos);
  if (table?.type.name !== "table" || row >= table.childCount) return false;
  let pos = tablePos + 1;
  for (let r = 0; r < row; r++) pos += table.child(r).nodeSize;
  const rowNode = table.child(row);
  if (col >= rowNode.childCount) return false;
  pos += 1;
  for (let c = 0; c < col; c++) pos += rowNode.child(c).nodeSize;
  // `pos` is the cell; +1 is inside it (its first paragraph), +2 inside that.
  return editor.commands.setTextSelection(pos + 2);
}
